import axios from 'axios';
import { logout } from './userActions';

// Only force client logout for session-expired responses.
const FRONTEND_AUTH_ERROR_CODES = new Set([ 1001 ]);

const hasAuthErrorCodePrefix = (message) => {
	if (typeof message !== 'string') return false;
	const match = message.trim().match(/^([0-9]+)\s*-\s+/);
	if (!match) return false;
	return FRONTEND_AUTH_ERROR_CODES.has(Number(match[1]));
};

const isAuthErrorResponse = (data) => {
	if (!data || typeof data !== 'object') return false;
	if (data.session_expired === true) return true;
	if (FRONTEND_AUTH_ERROR_CODES.has(Number(data.error_code))) return true;
	if (hasAuthErrorCodePrefix(data.msg)) return true;
	if (data.type === 'auth') return true;
	return false;
};

export const getIOCLists = () => async (dispatch) => {
	try {
		const config = {
			headers: {
				'Content-Type': 'application/json'
			}
		};

		const { data } = await axios.get(`/api/ioc`, config);
		// const data = {
		// 	FQDNCols: [ 'FQDN', 'Action' ],
		// 	FQDNRows: [ [ '211.110.12.203' ], [ '122.194.229.49' ] ],
		// 	IPSCols: [ 'IP Address', 'Action' ],
		// 	IPSRows: [ [ '211.110.12.201' ], [ '122.194.229.41' ] ],
		// 	status: true
		// };

		if (data && data.status) {
			const newFQDNRows = data && data.FQDNRows.reverse();
			const newIPSRows = data && data.IPSRows.reverse();
			const newHashRows = (data && data.HashRows ? data.HashRows : []).reverse();

			return {
				FQDNCols: data.FQDNCols,
				FQDNRows: newFQDNRows,
				IPSCols: data.IPSCols,
				IPSRows: newIPSRows,
				HashCols: data.HashCols,
				HashRows: newHashRows,
				// Pass the provider flag through — the IOC page gates its add/delete
				// controls on it; dropping it here hid the "+" buttons for everyone.
				isProvider: data.isProvider || false,
				status: true
			};
		} else if (data && !data.status) {
			dispatch(logout());
		} else {
			return {};
		}
	} catch (err) {
		return {};
	}
};

export const addIOCData = (formData) => async (dispatch) => {
	try {
		const config = {
			headers: {
				'Content-Type': 'application/json'
			}
		};

		const { data } = await axios.post(`/api/ioc`, formData, config);

		if (data && data.status) {
			const newFQDNRows = data && data.FQDNRows.reverse();
			const newIPSRows = data && data.IPSRows.reverse();
			const newHashRows = (data && data.HashRows ? data.HashRows : []).reverse();

			return {
				FQDNCols: data.FQDNCols,
				FQDNRows: newFQDNRows,
				IPSCols: data.IPSCols,
				IPSRows: newIPSRows,
				HashCols: data.HashCols,
				HashRows: newHashRows,
				// Pass the provider flag through — the IOC page gates its add/delete
				// controls on it; dropping it here hid the "+" buttons for everyone.
				isProvider: data.isProvider || false,
				status: true
			};
		} else if (data && !data.status) {
			// A validation error is not an auth failure — don't sign the user
			// out (the "adding logs me out" bug). Return the message instead.
			if (data.type === 'auth') {
				dispatch(logout());
				return {};
			}
			return { status: false, msg: data.msg || 'IOC update did not return success' };
		} else {
			return {};
		}
	} catch (err) {
		if (err?.response && [401, 403].includes(err.response.status)) {
			dispatch(logout());
			return {};
		}
		return { status: false, msg: err?.message || 'IOC request failed' };
	}
};

export const uploadIOCData = (link, formData) => async (dispatch) => {
	try {
		const config = {
			headers: {
				'Content-Type': 'application/json'
			}
		};

		const { data } = await axios.post(`/api/${link}`, formData, config);
		return data;
	} catch (err) {
		return {};
	}
};

export const getDeniedLists = (formData) => async (dispatch) => {
	try {
		const config = {
			headers: {
				'Content-Type': 'application/json'
			}
		};

		let url = '/api/denied';
		
		// If formData contains company parameter, add it as a query parameter
		if (formData && formData.get && formData.get('company')) {
			const company = formData.get('company');
			url = `/api/denied?company=${company}`;
		}

		const { data } = await axios.get(url, config);

		if (data && data.status) {
			const newCountriesRows = data && data.CountriesRows ? data.CountriesRows.reverse() : [];
			const newIPSRows = data && data.IPSRows ? data.IPSRows.reverse() : [];
			const newPortRows = data && data.PortRows ? data.PortRows.reverse() : [];
			const newHashRows = data && data.HashRows ? data.HashRows.reverse() : [];

			return {
				CountriesCols: data.CountriesCols || ['Country'],
				CountriesRows: newCountriesRows,
				IPSCols: data.IPSCols || ['IP Address'],
				IPSRows: newIPSRows,
				PortCols: data.PortCols || ['Port'],
				PortRows: newPortRows,
				HashCols: data.HashCols || ['Hash'],
				HashRows: newHashRows,
				status: true,
				selectedCompany: data.selectedCompany || "ALL",
				availableCompanies: data.availableCompanies || [],
				userCompany: data.userCompany,
				isProvider: data.isProvider || false
			};
		} else if (data && !data.status && data.type === 'auth') {
			// Only log out for authentication errors
			dispatch(logout());
			return {};
		} else {
			// Return empty data structure for other errors
			console.error('Error retrieving denied lists:', data?.message || 'Unknown error');
			return {
				CountriesCols: ['Country'],
				CountriesRows: [],
				IPSCols: ['IP Address'],
				IPSRows: [],
				PortCols: ['Port'],
				PortRows: [],
				status: true,
				selectedCompany: "ALL",
				availableCompanies: [],
				error: data?.message || 'Unknown error'
			};
		}
	} catch (err) {
		console.error('Exception in getDeniedLists:', err);
		// Return empty data structure on exception
		return {
			CountriesCols: ['Country'],
			CountriesRows: [],
			IPSCols: ['IP Address'],
			IPSRows: [],
			PortCols: ['Port'],
			PortRows: [],
			status: true,
			selectedCompany: "ALL",
			availableCompanies: [],
			error: err.message
		};
	}
};

export const addDeniedData = (formData) => async (dispatch) => {
	try {
		const config = {
			headers: {
				'Content-Type': 'application/json'
			}
		};

		const { data } = await axios.post(`/api/denied`, formData, config);

		if (data && data.status) {
			const newCountriesRows = data && data.CountriesRows ? data.CountriesRows.reverse() : [];
			const newIPSRows = data && data.IPSRows ? data.IPSRows.reverse() : [];
			const newPortRows = data && data.PortRows ? data.PortRows.reverse() : [];
			const newHashRows = data && data.HashRows ? data.HashRows.reverse() : [];

			return {
				CountriesCols: data.CountriesCols,
				CountriesRows: newCountriesRows,
				IPSCols: data.IPSCols,
				IPSRows: newIPSRows,
				PortCols: data.PortCols,
				PortRows: newPortRows,
				HashCols: data.HashCols || ['Hash'],
				HashRows: newHashRows,
				status: true,
				selectedCompany: data.selectedCompany || "ALL",
				availableCompanies: data.availableCompanies || [],
				userCompany: data.userCompany,
				isProvider: data.isProvider || false
			};
		} else if (data && !data.status) {
			// A validation error (bad IP, no company selected, …) is NOT an auth
			// failure — logging the user out here was the "adding to the Denied
			// List logs me out" bug. Only sign out on a genuine auth error; for
			// anything else, hand the message back so the page can show it.
			if (data.type === 'auth') {
				dispatch(logout());
				return {};
			}
			return { status: false, msg: data.msg || 'Denied list update did not return success' };
		} else {
			return {};
		}
	} catch (err) {
		if (err?.response && [401, 403].includes(err.response.status)) {
			dispatch(logout());
			return {};
		}
		return { status: false, msg: err?.message || 'Denied list request failed' };
	}
};

export const uploadDeniedData = (link, formData) => async (dispatch) => {
	try {
		const config = {
			headers: {
				'Content-Type': 'application/json'
			}
		};

		const { data } = await axios.post(`/api/${link}`, formData, config);
		return data;
	} catch (err) {
		return {};
	}
};

export const getWatcherSettings = () => async (dispatch) => {
	try {
		const config = {
			headers: {
				'Content-Type': 'application/json'
			}
		};

		const { data } = await axios.get(`/api/settings`, config);
		// const data = {
		// 	// msg: 'No rows',
		// 	SyslogServer: 'lorem ipsum',
		// 	SMTPServer: 'lorem ipsum',
		// 	SMTPPort: 'lorem ipsum',
		// 	SMTPSender: 'lorem ipsum',
		// 	SMTPRecipient: 'lorem ipsum',
		// 	SyslogEnable: 'False',
		// 	SMTPEnable: 'True',
		// 	MyName: 'lorem ipsum',
		// 	MyLat: 'lorem ipsum',
		// 	MyLong: 'lorem ipsum',
		// 	MyCountry: 'lorem ipsum',
		// 	MyState: 'lorem ipsum',
		// 	MyCity: 'lorem ipsum',
		// 	status: true
		// };

		if (data && data.status) {
			return data;
		} else if (data && !data.status) {
			if (isAuthErrorResponse(data)) {
				dispatch(logout());
			}
			return data;
		} else {
			return {};
		}
	} catch (err) {
		if (err?.response && [ 401, 403 ].includes(err.response.status)) {
			dispatch(logout());
		}
		return err.response && err.response.data ? err.response.data : {};
	}
};

export const saveWatcherSettings = (formData) => async (dispatch) => {
	try {
		const config = {
			headers: {
				'Content-Type': 'application/json'
			}
		};

		const { data } = await axios.post(`/api/settings`, formData, config);

		if (data && data.status) {
			return data;
		} else if (data && !data.status) {
			if (isAuthErrorResponse(data)) {
				dispatch(logout());
			}
			return data;
		} else {
			return {};
		}
	} catch (err) {
		if (err?.response && [ 401, 403 ].includes(err.response.status)) {
			dispatch(logout());
		}
		return err.response && err.response.data ? err.response.data : {};
	}
};

export const sendWatcherTestEmail = (payload = {}) => async (dispatch) => {
	try {
		const config = {
			headers: {
				'Content-Type': 'application/json'
			}
		};

		const { data } = await axios.post(`/api/email`, payload, config);

		if (data && data.status) {
			return data;
		} else if (data && !data.status) {
			if (isAuthErrorResponse(data)) {
				dispatch(logout());
			}
			return data;
		} else {
			return {};
		}
	} catch (err) {
		if (err?.response && [ 401, 403 ].includes(err.response.status)) {
			dispatch(logout());
		}
		return err.response && err.response.data ? err.response.data : {};
	}
};

export const sendWatcherTestSyslog = () => async (dispatch) => {
	try {
		const config = { headers: { 'Content-Type': 'application/json' } };
		const { data } = await axios.post(`/api/syslog/test`, {}, config);

		if (data && data.status) {
			return data;
		} else if (data && !data.status) {
			if (isAuthErrorResponse(data)) {
				dispatch(logout());
			}
			return data;
		} else {
			return {};
		}
	} catch (err) {
		if (err?.response && [ 401, 403 ].includes(err.response.status)) {
			dispatch(logout());
		}
		return err.response && err.response.data ? err.response.data : {};
	}
};
